import React, {
  cloneElement,
  isValidElement,
  Children,
  useMemo,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup } from '@mui/material';
import SolyticsBox from './SolyticsBox';
import SolyticsButton from './SolyticsButton';
import SolyticsPopper from './SolyticsPopper';
import SolyticsTypography from './SolyticsTypography';
import SolyticsTooltip from './SolyticsTooltip';
import { ReactComponent as KeyboardArrowDownIcon } from '../assets/KeyboardArrowDownIcon.svg';
import { ReactComponent as KeyboardArrowUpIcon } from '../assets/KeyboardArrowUpIcon.svg';

// Default props configuration for ButtonGroup component
// Defined outside the component to avoid recreation on every render
const DEFAULT_BUTTONGROUP_PROPS = {
  disableElevation: true,
  disableRipple: false,
  disableFocusRipple: true,
};

const DEFAULT_GROUP_TYPE = 'buttons';
const DEFAULT_DROPDOWN_LABEL = 'More';
const DROPDOWN_PANEL_BORDER_RADIUS = 0.5;
const DROPDOWN_PANEL_SHADOW =
  '0px 2px 4px -1px #0000000F, 0px 4px 6px -1px #0000001A';
const DROPDOWN_OPTION_BORDER_RADIUS = 0.5;
const MINIMUM_DROPDOWN_WIDTH = 120;

/**
 * SolyticsButtonGroup - A comprehensive button group component built on MUI ButtonGroup
 *
 * @component
 * @description
 * SolyticsButtonGroup is a wrapper around Material-UI's ButtonGroup component that groups
 * related buttons together with controlled prop exposure and custom styling.
 *
 * @param {React.ReactNode} children - Button components to be rendered within the group
 * @param {string} variant - The variant to use: 'contained' | 'outlined' | 'text'
 * @param {string} color - The color of the component: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
 * @param {string} size - The size of the buttons: 'small' | 'medium' | 'large'
 * @param {string} shape - Custom shape variant for grouped buttons: 'pill' | 'rectangle' | 'sharp'
 * @param {string} orientation - The orientation of the button group: 'horizontal' | 'vertical'
 * @param {string} groupType - Group behavior: 'buttons' | 'dropdown'
 * @param {string} dropdownLabel - Trigger text for dropdown group type
 * @param {Array<{label: string, onClick: function, disabled?: boolean, tooltip?: string}>} dropdownOptions - Dropdown options for groupType='dropdown'. Each option can include an optional `disabled` boolean to prevent interaction, and an optional `tooltip` string shown on hover when disabled.
 * @param {boolean} disabled - If true, all buttons in the group are disabled
 * @param {boolean} fullWidth - If true, the button group will take up the full width
 * @param {string} ariaLabel - Accessibility label for the button group
 * @param {string} id - HTML id attribute
 *
 * @returns {React.ReactElement} A button group element
 */
const SolyticsButtonGroup = ({
  children,
  id,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  shape,
  orientation = 'horizontal',
  groupType = DEFAULT_GROUP_TYPE,
  dropdownLabel = DEFAULT_DROPDOWN_LABEL,
  dropdownOptions = [],
  disabled = false,
  fullWidth = false,
  ariaLabel,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);

  const cornerRadius = useMemo(() => {
    switch (shape) {
      case 'pill':
        return '16px';
      case 'rectangle':
        return '4px';
      case 'sharp':
        return '0px';
      default:
        return '6px';
    }
  }, [shape]);

  const isDropdownGroup = groupType === 'dropdown';
  const menuId = `${id || ariaLabel || 'solytics-button-group'}-menu`;

  const handleDropdownToggle = useCallback((event) => {
    event.stopPropagation();
    setDropdownAnchorEl(event.currentTarget);
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const handleDropdownItemClick = useCallback((option) => {
    if (option?.disabled) return;
    setIsDropdownOpen(false);
    option?.onClick?.();
  }, []);

  const handleDropdownItemKeyDown = useCallback(
    (event, option) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleDropdownItemClick(option);
      }
    },
    [handleDropdownItemClick]
  );

  if (isDropdownGroup) {
    const isDropdownDisabled = disabled || dropdownOptions.length === 0;
    const triggerVariant = variant === 'text' ? 'outlined' : variant;

    return (
      <SolyticsBox
        sx={{
          display: 'inline-flex',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        <SolyticsButton
          id={id}
          variant={triggerVariant}
          color={color}
          size={size}
          disabled={isDropdownDisabled}
          fullWidth={fullWidth}
          aria-label={ariaLabel || dropdownLabel}
          aria-haspopup="menu"
          aria-expanded={isDropdownOpen}
          aria-controls={isDropdownOpen ? menuId : undefined}
          onClick={handleDropdownToggle}
          endIcon={
            isDropdownOpen ? (
              <KeyboardArrowUpIcon width={20} height={20} />
            ) : (
              <KeyboardArrowDownIcon width={20} height={20} />
            )
          }
        >
          {dropdownLabel}
        </SolyticsButton>

        <SolyticsPopper
          open={isDropdownOpen}
          anchorEl={dropdownAnchorEl}
          placement="bottom-start"
          transition
          enableClickAway
          onClickAway={handleDropdownClose}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
            {
              name: 'matchWidth',
              enabled: true,
              phase: 'beforeWrite',
              requires: ['computeStyles'],
              fn: ({ state }) => {
                /* eslint-disable no-param-reassign */
                state.styles.popper.width = `${Math.max(state.rects.reference.width, MINIMUM_DROPDOWN_WIDTH)}px`;
              },
            },
          ]}
        >
          <SolyticsBox
            id={menuId}
            role="menu"
            aria-label={ariaLabel || dropdownLabel}
            sx={(theme) => ({
              minWidth: 0,
              width: '100%',
              overflow: 'hidden',
              borderRadius: DROPDOWN_PANEL_BORDER_RADIUS,
              boxShadow: DROPDOWN_PANEL_SHADOW,
              background: theme.palette.background.default,
              border: `1px solid ${theme.palette.grey[60]}`,
              py: 1,
              boxSizing: 'border-box',
            })}
          >
            {dropdownOptions.map((option, _index) => {
              const isOptionDisabled = Boolean(option?.disabled);
              const tooltipText =
                isOptionDisabled && option?.tooltip
                  ? option.tooltip
                  : option?.label;

              return (
                <SolyticsTooltip
                  key={`${option?.label || 'option'}-${option.label}`}
                  text={tooltipText}
                  placement="right"
                  arrow
                >
                  <SolyticsBox
                    role="menuitem"
                    aria-disabled={isOptionDisabled}
                    tabIndex={isOptionDisabled ? -1 : 0}
                    onClick={() => handleDropdownItemClick(option)}
                    onKeyDown={(event) =>
                      handleDropdownItemKeyDown(event, option)
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mx: 0.5,
                      py: 1.5,
                      px: 2,
                      cursor: isOptionDisabled ? 'not-allowed' : 'pointer',
                      borderRadius: DROPDOWN_OPTION_BORDER_RADIUS,
                      overflow: 'hidden',
                      width: 'calc(100% - 8px)',
                      boxSizing: 'border-box',
                      opacity: isOptionDisabled ? 0.45 : 1,
                      ...(!isOptionDisabled && {
                        '&:hover': {
                          background: (theme) => theme.palette.grey[30],
                        },
                        '&:focus-visible': {
                          outline: (theme) =>
                            `2px solid ${theme.palette.lavenderBlue[80]}`,
                          outlineOffset: '2px',
                        },
                      }),
                    }}
                  >
                    <SolyticsTypography
                      color={(theme) =>
                        isOptionDisabled
                          ? theme.palette.text.disabled
                          : theme.palette.text.primary
                      }
                      ellipsis={true}
                    >
                      {option?.label}
                    </SolyticsTypography>
                  </SolyticsBox>
                </SolyticsTooltip>
              );
            })}
          </SolyticsBox>
        </SolyticsPopper>
      </SolyticsBox>
    );
  }

  // Styles based on variant
  const getVariantStyles = (theme) => {
    const radius = cornerRadius;
    const variantStyleMap = {
      text: {
        '& .MuiButtonGroup-grouped': {
          borderColor: 'transparent',
          borderRadius: '0px !important',
          '&:first-of-type': {
            borderTopLeftRadius: `${radius} !important`,
            borderBottomLeftRadius: `${radius} !important`,
          },
          '&:last-of-type': {
            borderTopRightRadius: `${radius} !important`,
            borderBottomRightRadius: `${radius} !important`,
          },
        },
      },
      contained: {
        '& .MuiButtonGroup-grouped': {
          borderRadius: '0px !important',
          '&:not(:last-of-type)': {
            borderRight: `1px solid ${theme.palette.divider}`,
          },
          '&:first-of-type': {
            borderTopLeftRadius: `${radius} !important`,
            borderBottomLeftRadius: `${radius} !important`,
          },
          '&:last-of-type': {
            borderTopRightRadius: `${radius} !important`,
            borderBottomRightRadius: `${radius} !important`,
          },
        },
      },
      outlined: {
        '& .MuiButtonGroup-grouped': {
          borderColor: '#E4E4E7',
          background: theme.palette.background?.default,
          borderRadius: '0px !important',
          '&:not(:last-of-type)': {
            borderRightColor: '#E4E4E7',
          },
          '&:first-of-type': {
            borderTopLeftRadius: `${radius} !important`,
            borderBottomLeftRadius: `${radius} !important`,
          },
          '&:last-of-type': {
            borderTopRightRadius: `${radius} !important`,
            borderBottomRightRadius: `${radius} !important`,
          },
        },
      },
    };
    return variantStyleMap[variant] ?? {};
  };

  // Styles based on orientation
  const getOrientationStyles = () => {
    const radius = cornerRadius;
    if (orientation === 'vertical') {
      return {
        '& .MuiButtonGroup-grouped': {
          '&:not(:last-of-type)': {
            borderRight: 'none',
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
          },
          '&:first-of-type': {
            borderTopLeftRadius: `${radius} !important`,
            borderTopRightRadius: `${radius} !important`,
            borderBottomLeftRadius: '0px !important',
          },
          '&:last-of-type': {
            borderBottomLeftRadius: `${radius} !important`,
            borderBottomRightRadius: `${radius} !important`,
            borderTopRightRadius: '0px !important',
          },
        },
      };
    }
    return {};
  };

  // Build props object for MUI ButtonGroup
  const buttonGroupProps = {
    ...DEFAULT_BUTTONGROUP_PROPS,
    ...(variant && { variant }),
    ...(color && { color }),
    ...(size && { size }),
    ...(orientation && { orientation }),
    ...{ disabled },
    ...(fullWidth && { fullWidth }),
    ...(ariaLabel && { 'aria-label': ariaLabel }),
    ...(id && { id }),
    sx: (theme) => ({
      ...getVariantStyles(theme),
      ...getOrientationStyles(),
      boxShadow: 'none',
      '& .MuiButtonGroup-grouped': {
        minWidth: 'auto',
        // Override any borderRadius from child buttons
        '& > *': {
          borderRadius: '0px !important',
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette?.primary?.main}`,
          outlineOffset: '2px',
          zIndex: 1,
        },
      },
      '&:hover': {
        boxShadow: 'none',
      },
    }),
  };

  // Clone children to ensure they use the group's props, (with fallback to their own props if provided)
  const clonedChildren = Children.map(children, (child) => {
    if (isValidElement(child)) {
      const {
        variant: childVariant,
        color: childColor,
        size: childSize,
        shape: childShape,
        ...restProps
      } = child.props;

      return cloneElement(child, {
        ...restProps,
        variant: childVariant ?? variant,
        color: childColor ?? color,
        size: childSize ?? size,
        shape: childShape ?? shape,
      });
    }
    return child;
  });

  return <ButtonGroup {...buttonGroupProps}>{clonedChildren}</ButtonGroup>;
};

SolyticsButtonGroup.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string,
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'error',
    'info',
    'warning',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  shape: PropTypes.oneOf(['pill', 'rectangle', 'sharp']),
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  groupType: PropTypes.oneOf(['buttons', 'dropdown']),
  dropdownLabel: PropTypes.string,
  dropdownOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      disabled: PropTypes.bool,
      tooltip: PropTypes.string,
    })
  ),
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default SolyticsButtonGroup;

/**
 * ## Features
 * - Controlled prop exposure - only specific props are exposed
 * - Support for horizontal and vertical orientations
 * - All standard MUI button variants: contained, outlined, text
 * - Consistent theming with other Solytics components
 * - Accessibility support with ARIA labels
 * - Focus-visible outline for keyboard navigation (WCAG compliant)
 * - Responsive design with fullWidth support
 *
 * ## Usage Guidelines
 * - Use ButtonGroup to group related actions together
 * - All children should be Button components (preferably SolyticsButton)
 * - Provide meaningful ariaLabel for accessibility
 * - Use vertical orientation for stacked layouts
 *
 * ## Variants
 * - `contained`: Buttons with solid background grouped together
 * - `outlined`: Buttons with border grouped together
 * - `text`: Text-only buttons grouped together
 *
 * ## Orientation
 * - `horizontal`: Buttons arranged side by side (default)
 * - `vertical`: Buttons stacked vertically
 *
 * ## Accessibility Features
 * - Keyboard navigation support with focus-visible outlines
 * - ARIA label support for screen readers
 * - WCAG 2.1 AA compliant focus indicators
 * - Proper grouping semantics
 *
 * ## Examples
 *
 * ```jsx
 * // Basic horizontal button group
 * <SolyticsButtonGroup variant="contained" ariaLabel="Text formatting">
 *   <SolyticsButton>Bold</SolyticsButton>
 *   <SolyticsButton>Italic</SolyticsButton>
 *   <SolyticsButton>Underline</SolyticsButton>
 * </SolyticsButtonGroup>
 *
 * // Outlined button group
 * <SolyticsButtonGroup variant="outlined" color="primary">
 *   <SolyticsButton>Option 1</SolyticsButton>
 *   <SolyticsButton>Option 2</SolyticsButton>
 *   <SolyticsButton>Option 3</SolyticsButton>
 * </SolyticsButtonGroup>
 *
 * // Vertical button group
 * <SolyticsButtonGroup
 *   orientation="vertical"
 *   variant="contained"
 *   ariaLabel="Action menu"
 * >
 *   <SolyticsButton>Create</SolyticsButton>
 *   <SolyticsButton>Edit</SolyticsButton>
 *   <SolyticsButton>Delete</SolyticsButton>
 * </SolyticsButtonGroup>
 *
 * // Full width button group
 * <SolyticsButtonGroup fullWidth variant="outlined">
 *   <SolyticsButton>Left</SolyticsButton>
 *   <SolyticsButton>Center</SolyticsButton>
 *   <SolyticsButton>Right</SolyticsButton>
 * </SolyticsButtonGroup>
 *
 * // Disabled button group
 * <SolyticsButtonGroup disabled variant="contained">
 *   <SolyticsButton>Button 1</SolyticsButton>
 *   <SolyticsButton>Button 2</SolyticsButton>
 *   <SolyticsButton>Button 3</SolyticsButton>
 * </SolyticsButtonGroup>
 *
 * // With different sizes
 * <SolyticsButtonGroup size="small" variant="outlined">
 *   <SolyticsButton>Small</SolyticsButton>
 *   <SolyticsButton>Buttons</SolyticsButton>
 * </SolyticsButtonGroup>
 *
 * <SolyticsButtonGroup size="large" variant="contained" color="secondary">
 *   <SolyticsButton>Large</SolyticsButton>
 *   <SolyticsButton>Buttons</SolyticsButton>
 * </SolyticsButtonGroup>
 *
 * // With icons in buttons
 * <SolyticsButtonGroup variant="outlined" ariaLabel="View options">
 *   <SolyticsButton startIcon={<ViewListIcon />}>List</SolyticsButton>
 *   <SolyticsButton startIcon={<ViewModuleIcon />}>Grid</SolyticsButton>
 *   <SolyticsButton startIcon={<ViewQuiltIcon />}>Quilt</SolyticsButton>
 * </SolyticsButtonGroup>
 *
 * // Color variants
 * <SolyticsButtonGroup color="success" variant="contained">
 *   <SolyticsButton>Approve</SolyticsButton>
 *   <SolyticsButton>Accept</SolyticsButton>
 * </SolyticsButtonGroup>
 *
 * <SolyticsButtonGroup color="error" variant="outlined">
 *   <SolyticsButton>Delete</SolyticsButton>
 *   <SolyticsButton>Remove</SolyticsButton>
 * </SolyticsButtonGroup>
 * ```
 *
 * ## Best Practices
 * - Limit the number of buttons in a group to 3-5 for optimal UX
 * - Use consistent button sizes within a group
 * - Provide clear, concise labels for each button
 * - Always include ariaLabel for screen reader accessibility
 * - Use appropriate color variants to indicate button purpose
 * - Consider vertical orientation for mobile layouts
 * - Don't mix different button variants within the same group
 *
 * ## Styling Notes
 * - Custom sx, classes props are not exposed to maintain consistency
 * - Ripple and elevation effects are controlled internally
 * - Theming is handled through theme files (light.js, dark.js, base.js)
 * - Border radius and spacing are standardized across all variants
 * - Focus indicators follow WCAG 2.1 AA guidelines
 */
